import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Quote } from "lucide-react";
import { Reveal } from "./Reveal";
import { STATS, TESTIMONIALS, MARQUEE } from "../../data";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);
  const decimals = value % 1 !== 0 ? 1 : 0;

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} className="font-heading text-5xl font-black text-white md:text-7xl">
      {display.toFixed(decimals)}
      <span className="text-[#00F0FF]">{suffix}</span>
    </span>
  );
};

const Marquee = () => (
  <div className="flex overflow-hidden border-y border-white/10 bg-[#00F0FF] py-4">
    <div className="animate-marquee flex shrink-0 items-center whitespace-nowrap">
      {[...MARQUEE, ...MARQUEE].map((t, i) => (
        <span
          key={i}
          className="mx-6 flex items-center gap-6 font-heading text-3xl font-black uppercase tracking-tight text-black md:text-4xl"
        >
          {t}
          <span className="text-black/40">/</span>
        </span>
      ))}
    </div>
  </div>
);

export const Results = () => (
  <section id="results" data-testid="results-section" className="relative bg-[#080808]">
    <Marquee />

    {/* Stat band */}
    <div className="border-b border-white/10 bg-[#050505] py-16 md:py-20">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-10 px-6 md:grid-cols-4 md:px-12">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1} className="text-center md:text-left" data-testid={`stat-${i}`}>
            <Counter value={s.value} suffix={s.suffix} />
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white/45">
              {s.label}
            </p>
          </Reveal>
        ))}
      </div>
    </div>

    {/* Testimonials */}
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      <Reveal>
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#00F0FF]">
          [ What Athletes Say ]
        </span>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <Reveal
            key={t.name}
            delay={i * 0.12}
            className={`flex flex-col border border-white/10 bg-white/[0.015] p-8 transition-colors duration-300 hover:border-[#00F0FF]/40 ${
              i === 1 ? "lg:-translate-y-6" : ""
            }`}
            data-testid={`testimonial-${i}`}
          >
            <Quote className="text-[#00F0FF]" size={28} />
            <p className="mt-6 flex-1 font-body text-base leading-relaxed text-white/80">
              "{t.quote}"
            </p>
            <div className="mt-8 border-t border-white/10 pt-5">
              <p className="font-heading text-lg font-bold uppercase tracking-tight text-white">
                {t.name}
              </p>
              <p className="font-mono text-[11px] uppercase tracking-wider text-white/40">
                {t.role}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Results;
